import React from 'react'
import { useSelector } from 'react-redux'
import { CCol, CRow } from '@coreui/react-pro'
import { format } from 'date-fns'
import { dateUSFormat } from '../../../utils/dateTimeUtils'
import { shouldHidePrivateNote } from '../../../utils/sharedChartFunctions'
import { privateNoteErrorMessage } from '../../../views/pages/ProgressNote/progressNoteConstants'
import FEATURES from '../../../utils/featuresConstants'
import { getAge } from '../../../utils/sharedFunctions'

export default function DisplayProgressNote({ chartData, visitDetails }) {
  const features = useSelector((state) => state?.user?.features)
  const userId = useSelector((state) => state?.user?.user?._id)
  const canUseNotes = features?.includes(FEATURES.VisitNotesPOC)

  return (
    <div className="preview-note-template-visit-history">
      {shouldHidePrivateNote(chartData, userId) ? (
        <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
          {privateNoteErrorMessage}
        </div>
      ) : (
        <div className="note-history-holder" style={{ justifyContent: 'flex-start' }}>
          <div className="main-note">
            {/* ORU has their own headers, so don't show our custom one if ORU */}
            {canUseNotes && (
              <CRow className="w-100">
                <CCol lg="8" className="pl-2 mb-2">
                  {visitDetails?.patient_name && (
                    <>
                      <p>
                        SERVICE DATE:{' '}
                        {visitDetails?.service_date_from
                          ? dateUSFormat(visitDetails?.service_date_from)
                          : ''}
                      </p>
                      <p>
                        PATIENT: {visitDetails?.patient_name} -
                        <span style={{ display: 'block' }}>{`(${visitDetails?.gender} | ${
                          visitDetails?.patient_dob ? dateUSFormat(visitDetails?.patient_dob) : ''
                        } | ${getAge(
                          visitDetails?.patient_dob,
                          visitDetails?.service_date_from,
                        )} y/o)`}</span>
                      </p>
                      <p>FACILITY: {visitDetails?.facility_name} </p>
                      {chartData?.signed_on && (
                        <p>DATE SIGNED: {dateUSFormat(chartData?.signed_on)} </p>
                      )}
                    </>
                  )}
                </CCol>
                <CCol lg="4" className="ml-2 justify-content-right">
                  <p>{visitDetails?.rendering_provider?.organization_name || ''}</p>
                  <p>{visitDetails?.rendering_provider?.phone || ''}</p>
                  <p>{visitDetails?.incident_provider?.organization_name || ''}</p>
                  <p>{visitDetails?.incident_provider?.phone || ''}</p>
                </CCol>
              </CRow>
            )}
            {chartData?.title_print && (
              <center>
                <div
                  dangerouslySetInnerHTML={{
                    __html: chartData?.title_print,
                  }}
                  className="note-title-txt"
                ></div>
              </center>
            )}

            {chartData?.data?.map((section, index) => {
              if (section?.skip) {
                return <React.Fragment key={index + '-' + section?.title}></React.Fragment>
              }
              return (
                <div key={index + '-' + section?.title}>
                  {section?.title !== 'Diagnosis (ICD10)' &&
                    section?.title !== 'E&M and procedure codes' && (
                      <>
                        <div>
                          <div>
                            {/* Don't show note text title if ORU */}
                            {canUseNotes &&
                              (section?.optional ? (
                                section?.skip === false && <b> {section.title} </b>
                              ) : (
                                <b> {section.title} </b>
                              ))}

                            {(section?.values || section?.instructions) && (
                              <div
                                className="section-item-holder pl-2"
                                style={{ minHeight: '1em', marginBottom: '16px' }}
                              >
                                {section?.values ? (
                                  <p
                                    dangerouslySetInnerHTML={{
                                      __html: Object.values(section?.values)?.join(',\n'),
                                    }}
                                  />
                                ) : (
                                  <p
                                    dangerouslySetInnerHTML={{
                                      __html: section?.instructions,
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                </div>
              )
            })}
          </div>
          {chartData?.signed_on && (
            <div className="p-note-footer">
              <div className="date">
                ** Document e-signed by{' '}
                {chartData?.signed_provider?.first_name
                  ? chartData?.signed_provider?.first_name +
                    ' ' +
                    chartData?.signed_provider?.last_name
                  : ''}{' '}
                , on {format(new Date(chartData?.signed_on), 'MMM dd, yyyy hh:mm a')}
                **
              </div>
            </div>
          )}
          {chartData?.cosigned_on && chartData?.cosigned_provider && (
            <div className="p-note-footer">
              <div className="date">
                ** Document e-signed by{' '}
                {chartData?.cosigned_provider?.first_name
                  ? chartData?.cosigned_provider?.first_name +
                    ' ' +
                    chartData?.cosigned_provider?.last_name
                  : ''}{' '}
                , on {format(new Date(chartData?.cosigned_on), 'MMM dd, yyyy hh:mm a')}
                **
              </div>
            </div>
          )}
          {chartData?.addendums?.map((record, index) => {
            let provider_name =
              chartData?.signed_provider?.first_name +
              ' ' +
              chartData?.signed_provider?.last_name +
              ' ' +
              chartData?.signed_provider?.title

            if (record.provider_id) {
              let matchedProvider = chartData?.addendum_providers?.find(
                (prov) => String(prov?._id) === String(record.provider_id),
              )
              if (matchedProvider) {
                provider_name =
                  matchedProvider.first_name +
                  ' ' +
                  matchedProvider.last_name +
                  ' ' +
                  matchedProvider.title
              }
            }
            return (
              <div key={'addendum' + index} style={{ padding: '8px' }}>
                <div style={{ fontWeight: 'bolder' }}>
                  Addendum ({index + 1} of {chartData.addendums.length})
                </div>
                <p style={{ fontSize: '14px' }}>{record.addendum} </p>
                <div className="p-note-footer">
                  <div className="date">
                    ** Document e-signed by {provider_name} on{' '}
                    {new Date(record?.created_on).toLocaleTimeString('en-US', {
                      timeZone: 'America/Los_Angeles',
                      timeZoneName: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true,
                    })}
                    **
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
